// Disclaimer.js
import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './Footer';
import Nav from './Nav';

function Disclaimer() {
  return (
    <>
      <Nav />
      <Helmet>
        <title>Disclaimer | Speak Your Menu</title>
        <meta
          name="description"
          content="Speak Your Menu assessment results are estimates based on your inputs and published research. Learn about assumptions, limitations, and how to use results responsibly."
        />
        <link rel="canonical" href="https://speakyourmenu.com/disclaimer" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
            { "@type": "ListItem", position: 2, name: "Disclaimer", item: "https://speakyourmenu.com/disclaimer" },
          ],
        })}</script>
      </Helmet>

      <main id="main-content" tabIndex="-1" className="container my-5">
        <h1 className="mb-4">Disclaimer</h1>
        <p className="text-muted">Last Updated: September 16, 2025</p>

        <section>
          <h2>1) Purpose</h2>
          <p>
            This page explains how to interpret estimates produced by the Speak Your Menu
            assessment (the “Assessment”). It also summarizes key assumptions, limitations,
            and responsibilities when using the Assessment and any reports or PDFs generated
            from it (collectively, the “Results”).
          </p>
        </section>

        <section>
          <h2>2) Estimates, Not Guarantees</h2>
          <p>
            The Results are <strong>projections/estimates only</strong> based on your inputs and
            published learning-science research. Actual outcomes vary and depend on factors
            beyond our control. <strong>No specific outcome is promised or guaranteed.</strong>
          </p>
        </section>

        <section>
          <h2>3) Key Assumptions</h2>
          <p>Unless stated otherwise, the Results generally assume:</p>
          <ul>
            <li>Accurate baseline inputs (scores, practices, adoption readiness).</li>
            <li>Regular usage of Speak Your Menu’s training features (e.g., spaced review, retrieval practice, feedback).</li>
            <li>Reasonable implementation fidelity (staff participation, adherence to recommendations).</li>
            <li>Comparable operating conditions to those studied in the underlying research.</li>
          </ul>
          <p>
            If these assumptions are not met (e.g., sporadic use, inaccurate inputs, major operational changes),
            your outcomes may differ significantly from the Results.
          </p>
        </section>

        <section>
          <h2>4) Factors Outside Our Control</h2>
          <ul>
            <li>Staff turnover, scheduling changes, or inconsistent participation.</li>
            <li>Menu complexity, frequent updates, or non-standard operating constraints.</li>
            <li>Managerial follow-through, coaching quality, and accountability systems.</li>
            <li>External variables (seasonality, competition, pricing, foot traffic, marketing mix).</li>
          </ul>
        </section>

        <section>
          <h2>5) Research Basis &amp; Limitations</h2>
          <p>
            Our methodology draws on published research in learning science and second-language acquisition.
            Effect sizes and confidence ranges vary by study context, participant profile, and measurement delay.
            Where our model offers “scenarios” (e.g., conservative, typical, ambitious, best-case), they reflect
            different <em>assumptions</em> about engagement and implementation fidelity—not promises of performance.
          </p>
        </section>

        <section>
          <h2>6) No Professional Advice</h2>
          <p>
            The Results are provided for informational purposes only and do not constitute legal, financial,
            medical, HR, or other professional advice. You should consult qualified professionals for decisions
            that could materially impact your business.
          </p>
        </section>

        <section>
          <h2>7) Use of the Assessment</h2>
          <ul>
            <li>Do not misrepresent the Results as guarantees or verified audits.</li>
            <li>Do not publish the underlying model, code, or proprietary content without prior written consent.</li>
            <li>When sharing externally, include the estimate context (scenario label) and this Disclaimer URL.</li>
          </ul>
        </section>

        <section>
          <h2>8) Privacy &amp; Data</h2>
          <p>
            The Assessment may collect business and contact information (e.g., assessment responses and email to deliver your Results).
            Data practices are described in our <a href="/privacy">Privacy Policy</a>, which explains what we collect, how we use it,
            how we share it, and your choices.
          </p>
        </section>

        <section>
          <h2>9) No Warranties; Limitation of Liability</h2>
          <p>
            The Assessment and Results are provided “as is” and “as available,” without warranties of any kind. To the maximum extent
            permitted by law, Speak Your Menu disclaims all warranties, express or implied, including merchantability, fitness for a
            particular purpose, non-infringement, accuracy, completeness, or reliability of the Results.
          </p>
          <p>
            To the maximum extent permitted by law, Speak Your Menu shall not be liable for any indirect, incidental, special,
            consequential, exemplary, or punitive damages, or for lost profits, revenues, data, goodwill, or business opportunities
            arising from or related to use of the Assessment or reliance on the Results.
          </p>
        </section>

        <section>
          <h2>10) Versioning &amp; Updates</h2>
          <p>
            We periodically update our methodology, coefficients, and scenarios. Each PDF or exported report may include an update date
            and/or version reference. We may modify this Disclaimer from time to time; the “Last Updated” date above reflects the latest
            revision.
          </p>
        </section>

        <section>
          <h2>11) Scope &amp; Precedence</h2>
          <p>
            This Disclaimer is part of and incorporated into our <a href="/terms">Terms of Service</a>. If there is any conflict between
            this Disclaimer and the Terms, the Terms will govern.
          </p>
        </section>

        <section>
          <h2>12) Contact</h2>
          <p>
            Questions? Contact us at <a href="mailto:support@speakyourmenu.com">support@speakyourmenu.com</a>.
          </p>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Disclaimer;
